import { useEffect, useMemo, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import {
  COLUMN_GAP,
  COLUMN_WIDTH,
  HEIGHT_OF_ONE_HOUR,
  MOUSE_CONSTRAINT_DISTANCE,
  ONE_HOUR,
  TIMELINE_WIDTH,
} from "@miplatform/constants";
import {
  getAdjustedHeight,
  getTimeIntervals,
  isToday,
} from "@miplatform/utils";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Box, Typography, useTheme } from "@mui/material";
import { Bucket } from "../Bucket/Bucket";
import { Buckets } from "../Buckets/Buckets";
import { ColumnHeaderContainer } from "../ColumnHeaderContainer/ColumnHeaderContainer";
import { CurrentTimeIndicator } from "../CurrentTimeIndicator/CurrentTimeIndicator";
import { DayColumn } from "../DayColumn/DayColumn";
import { Event } from "../Event/Event";
import { TimeGutter } from "../TimeGutter/TimeGutter";
import type { SchedulerEvent, SchedulerViewProps } from "../types";
import { useDaysViewViewModel } from "./useDaysViewViewModel";

export function DaysView({
  buckets = [],
  data = [],
  dateRange = { startDate: "", endDate: "" },
  EventCard,
  eventClassName,
  increment = ONE_HOUR,
  onColumnHeaderClick,
  onDrag,
  onEventCreate,
  onEventUpdate,
  users,
  selectedTechnician,
  time,
  hasModalOpen,
  zoom = 1,
}: Readonly<SchedulerViewProps>) {
  const intervals = useMemo(() => getTimeIntervals(increment), [increment]);
  const adjustedHeight = useMemo(
    () => getAdjustedHeight(increment, zoom),
    [increment, zoom]
  );
  const currentTechnicianData = useMemo(
    () => users?.find((user) => user.id === selectedTechnician?.id),
    [users, selectedTechnician]
  );

  const {
    columns,
    draggingEvent,
    selectedEventId,
    setSelectedEventId,
    getDatesInRange,
    handleScroll,
    handleDragStart,
    handleDragCancel,
    snapModifier,
    prepareColumns,
    handleDragEnd,
  } = useDaysViewViewModel({
    onEventUpdate,
    onEventCreate,
    zoom,
    currentTechnicianData,
    intervals,
  });

  const columnHeaderAreaRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const datesInRange = useMemo(
    () =>
      dateRange ? getDatesInRange(dateRange.startDate, dateRange.endDate) : [],
    [dateRange]
  );

  useEffect(() => {
    if (data) {
      prepareColumns({ data, datesInRange });
    }
  }, [data, datesInRange]);

  useEffect(() => {
    onDrag?.(draggingEvent);
  }, [draggingEvent]);

  useEffect(() => {
    if (containerRef?.current) {
      containerRef.current.scrollTo({ top: HEIGHT_OF_ONE_HOUR * 7 });
    }
  }, []);

  const { classes } = useStyles();
  const theme = useTheme();

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: MOUSE_CONSTRAINT_DISTANCE,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      distance: MOUSE_CONSTRAINT_DISTANCE,
    },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  return (
    <Box className={classes.container}>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
        modifiers={[snapModifier]}
        autoScroll={false}
      >
        <Box className={classes.viewContainer} ref={containerRef}>
          <Box className={classes.stickyTopArea}>
            <Box
              className={`${classes.columnsContainer} ${classes.columnHeaders}`}
              ref={columnHeaderAreaRef}
              onScroll={handleScroll(calendarRef)}
            >
              {datesInRange.map(({ day, date }) => (
                <ColumnHeaderContainer
                  key={date}
                  onClick={() => {
                    onColumnHeaderClick?.({ date });
                  }}
                >
                  <Box className={classes.header}>
                    <Typography
                      fontSize={theme.fontSize.xsmall}
                      fontWeight={theme.fontWeight.bold}
                    >
                      {day}
                    </Typography>
                    {isToday(date) ? (
                      <Typography
                        fontSize={theme.fontSize.xsmall}
                        color={theme.palette.blue.main}
                      >
                        Today
                      </Typography>
                    ) : (
                      <Typography
                        fontSize={theme.fontSize.xsmall}
                        color={theme.palette.grey.main}
                      >
                        {date}
                      </Typography>
                    )}
                  </Box>
                </ColumnHeaderContainer>
              ))}
            </Box>
          </Box>
          <Box className={classes.calendarContainer} data-testid="days-view">
            <TimeGutter intervals={intervals} adjustedHeight={adjustedHeight} />
            <Box
              className={classes.columnsContainer}
              ref={calendarRef}
              onScroll={handleScroll(columnHeaderAreaRef)}
            >
              {columns.map(([key, events, deactivatedIntervals]) => (
                <DayColumn
                  id={key}
                  adjustedHeight={adjustedHeight}
                  key={key}
                  intervals={intervals}
                  deactivatedIntervals={deactivatedIntervals}
                >
                  {events.map((event) => (
                    <Event
                      key={event.id}
                      hasModalOpen={hasModalOpen}
                      event={event}
                      className={
                        typeof eventClassName === "function"
                          ? eventClassName(event)
                          : eventClassName
                      }
                      onEventUpdate={
                        onEventUpdate as
                          | ((item: SchedulerEvent, updates: unknown) => void)
                          | undefined
                      }
                      onSelect={() => setSelectedEventId(event.id)}
                      onDeselect={handleDragCancel}
                      zoom={zoom}
                    >
                      {EventCard && (
                        <EventCard
                          event={event}
                          isSelected={selectedEventId === event.id}
                        />
                      )}
                    </Event>
                  ))}
                </DayColumn>
              ))}
            </Box>
            <CurrentTimeIndicator time={time ?? undefined} zoom={zoom} />
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          flex={0}
          className={classes.bucketContainer}
        >
          <Buckets>
            {buckets.map((bucket) => (
              <Bucket key={bucket.id} bucket={bucket}>
                {bucket.data?.map((event) => (
                  <Event
                    key={event.id}
                    event={event}
                    hasModalOpen={hasModalOpen}
                    className={
                      typeof eventClassName === "function"
                        ? eventClassName(event)
                        : eventClassName
                    }
                    onSelect={() => setSelectedEventId(event.id)}
                    onDeselect={handleDragCancel}
                    isInBucket
                  >
                    {EventCard && (
                      <EventCard
                        event={event}
                        isSelected={selectedEventId === event.id}
                        isInBucket
                      />
                    )}
                  </Event>
                ))}
              </Bucket>
            ))}
          </Buckets>
        </Box>
        {draggingEvent && (
          <DragOverlay modifiers={[snapModifier]}>
            <Event
              className={
                typeof eventClassName === "function"
                  ? eventClassName(draggingEvent)
                  : eventClassName
              }
              event={draggingEvent}
              onDeselect={handleDragCancel}
              zoom={zoom}
              isOverLay
            >
              {EventCard && (
                <EventCard
                  event={draggingEvent}
                  isSelected={selectedEventId === draggingEvent.id}
                />
              )}
            </Event>
          </DragOverlay>
        )}
      </DndContext>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    overflow: "hidden",
  },
  viewContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "auto",
    maxWidth: "fit-content",
    backgroundColor: theme.palette.background.default,
    "&::-webkit-scrollbar": { display: "none" } /* webkit */,
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,
  },
  calendarContainer: {
    display: "flex",
    position: "relative",
    flex: 1,
  },
  columnsContainer: {
    flexWrap: "nowrap",
    display: "flex",
    gap: COLUMN_GAP,
    flex: 1,
  },
  columnHeaders: {
    marginLeft: TIMELINE_WIDTH,
  },
  header: {
    boxSizing: "border-box",
    margin: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    width: COLUMN_WIDTH - 8,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  bucketContainer: {
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    zIndex: 1,
  },
  stickyTopArea: {
    backgroundColor: theme.palette.background.default,
    position: "sticky",
    top: 0,
    zIndex: 99,
  },
}));
